/**
 * WOW pricing — payload builder for `/flight/v2/fetch-dynamic-content`.
 * Ported from flights-pwa src/utils/srp/index.ts → getFetchContentPayload.
 *
 * The endpoint expects a compact payload: top-N price-sorted cards per
 * journey, the subTravelOptions/fares/flights they touch, and (for RT or
 * interstitial) only the user-selected cards.
 */

import { isWowTest2CleartripVariant } from './wow'
import { getLeadFareIdForCard, getSpecialRTFareContext } from './helper'

export interface FetchContentSearchData {
    searchIntent?: Record<string, any>
    sortData?: { price_up?: Record<string, number[]> }
    cards?: Record<string, any[]>
    subTravelOptions?: Record<string, any>
    fares?: Record<string, any>
    flights?: Record<string, any>
    travelOptionIdsToFareIdsMap?: Record<string, any>
    specialRTFareMap?: Record<string, any>
    supercoins?: { balance?: number }
    searchId?: string
    dataId?: string
    trackingId?: string
    configData?: { initialWowSolutionsCount?: number }
}

export interface FetchContentFlowConditions {
    isIntl?: boolean
    tripType?: string
    isCfw?: boolean
    isInterstitial?: boolean
    wowPricingVariant?: string
    /**
     * Override cards (used by progressive batches): caller hands the exact
     * cards to fetch, bypassing the price_up top-N sort.
     */
    overrideCards?: Record<string, Array<{ cardId: string; travelOptionId: string; fareId?: string }>>
}

const DEFAULT_CARD_COUNT = 100
const DEFAULT_SUB_TRAVEL_OPTION_COUNT = 4

function findCardInSearchData(
    cardRef: { cardId?: string | number; travelOptionId?: string },
    searchData: FetchContentSearchData,
    journeyKey?: string
) {
    const journeyCards = journeyKey ? searchData?.cards?.[journeyKey] : undefined
    const cardsToSearch = journeyKey
        ? journeyCards || []
        : [...(searchData?.cards?.J1 || []), ...(searchData?.cards?.J2 || [])]

    return cardsToSearch.find(
        card =>
            card?.travelOptionId === cardRef.travelOptionId ||
            card?.cardId === cardRef.cardId
    )
}

function extractCardForWowPayload(
    card: { cardId?: string | number; travelOptionId?: string; subTravelOptionIds?: string[] },
    searchData: FetchContentSearchData,
    specialRtContext?: { isSpecialRT?: boolean; specialRtLeadFareId?: string }
) {
    const leadFareId = getLeadFareIdForCard(
        card as any,
        searchData?.subTravelOptions || {},
        searchData?.fares || {},
        specialRtContext
    )

    return {
        cardId: card?.cardId,
        travelOptionId: card?.travelOptionId,
        ...(leadFareId ? { fareId: leadFareId } : {}),
    }
}

export function getFetchContentPayload(
    searchData: FetchContentSearchData,
    flowConditions?: FetchContentFlowConditions,
    selectedCards?: Record<string, any[]>
) {
    const priceUp = searchData?.sortData?.price_up || {}
    const { supercoins = {} } = searchData || {}

    // Reference reads card/STO counts from a feature-config widget. Desktop
    // doesn't have that flag plumbed, so we use the safer constants here and
    // honour an explicit override on the search response if the BE provides it.
    const initialWowSolutionsCount =
        searchData?.configData?.initialWowSolutionsCount ?? DEFAULT_CARD_COUNT
    const subTravelOptionCount = DEFAULT_SUB_TRAVEL_OPTION_COUNT

    const sortedCardData: Record<string, any[]> = {}
    const subTravelOptionsMap: Record<string, any> = {}
    const faresMap: Record<string, any> = {}
    const flightsMap: Record<string, any> = {}

    let isDomesticOnewayNonCFW = false
    let isRoundTrip = false
    let isInterstitial = false
    if (flowConditions) {
        const {
            isIntl = false,
            tripType = '',
            isCfw = false,
            isInterstitial: interstitialFlag = false
        } = flowConditions
        isDomesticOnewayNonCFW = !isIntl && tripType === 'oneway' && !isCfw
        isRoundTrip = tripType === 'roundtrip'
        isInterstitial = interstitialFlag
    }

    const useSelectedCards =
        (isRoundTrip || isInterstitial) && selectedCards && Object.keys(selectedCards).length > 0

    let travelOptionIdsToFareIdsMapData: Record<string, any> = {}
    const j1SelectedCards = selectedCards?.['J1'] || []
    const j2SelectedCards = selectedCards?.['J2'] || []
    const specialRtContext =
        j1SelectedCards[0] && j2SelectedCards[0] && searchData?.specialRTFareMap
            ? getSpecialRTFareContext({
                  onwardCard: j1SelectedCards[0],
                  returnCard: j2SelectedCards[0],
                  subTravelOptions: searchData?.subTravelOptions || {},
                  fares: searchData?.fares || {},
                  specialRTFareMap: searchData.specialRTFareMap
              })
            : undefined

    const overrideCards = flowConditions?.overrideCards
    if (overrideCards && Object.keys(overrideCards).length > 0) {
        // Progressive batch: caller picked the exact cards.
        Object.keys(overrideCards).forEach(journeyKey => {
            sortedCardData[journeyKey] = overrideCards[journeyKey].map(cardRef => {
                const fullCard = findCardInSearchData(cardRef, searchData, journeyKey) || cardRef
                return extractCardForWowPayload(fullCard, searchData)
            })
        })
    } else if (useSelectedCards && selectedCards) {
        Object.keys(selectedCards).forEach(journeyKey => {
            const journeySelectedCards = selectedCards[journeyKey] || []
            const extractedCards = journeySelectedCards.map((card: any) => {
                if (card?.subTravelOptionIds?.[0] && searchData?.subTravelOptions) {
                    const subTravelOptionId = card.subTravelOptionIds[0]
                    const subTravelOption = searchData.subTravelOptions[subTravelOptionId]
                    if (subTravelOption && !subTravelOptionsMap[subTravelOptionId]) {
                        subTravelOptionsMap[subTravelOptionId] = {
                            sequenceToFlightIdMap: subTravelOption.sequenceToFlightIdMap,
                            fareIds: subTravelOption.fareIds
                        }
                    }
                }
                return extractCardForWowPayload(card, searchData, specialRtContext)
            })
            sortedCardData[journeyKey] = extractedCards
        })

        // travelOptionIdsToFareIdsMap for roundtrip
        const j1Cards = j1SelectedCards
        const j2Cards = j2SelectedCards
        if (j1Cards.length > 0 && j2Cards.length > 0) {
            const j1SubTravelOptionId = j1Cards[0]?.subTravelOptionIds?.[0]
            const j2SubTravelOptionId = j2Cards[0]?.subTravelOptionIds?.[0]
            if (j1SubTravelOptionId && j2SubTravelOptionId) {
                const key = `[${j1SubTravelOptionId}, ${j2SubTravelOptionId}]`
                if (searchData?.travelOptionIdsToFareIdsMap?.[key]) {
                    travelOptionIdsToFareIdsMapData[key] =
                        searchData.travelOptionIdsToFareIdsMap[key]
                    const fareIdsArray = searchData.travelOptionIdsToFareIdsMap[key]
                    if (Array.isArray(fareIdsArray) && searchData?.fares) {
                        fareIdsArray.forEach((fareId: string) => {
                            const fare = searchData.fares?.[fareId]
                            if (fare && !faresMap[fareId]) {
                                faresMap[fareId] = {
                                    fareId: fare.fareId || fareId,
                                    fareGroup: fare.fareGroup,
                                    fareCategory: fare.fareCategory,
                                    pricing: fare.pricing,
                                    subTravelOptionFare: fare.subTravelOptionFare
                                }
                            }
                        })
                    }
                }
            }
        }
    } else {
        // Default path: top-N from price_up indices per journey.
        Object.keys(priceUp).forEach(journeyKey => {
            const journeyIndices = priceUp[journeyKey] || []
            const journeyCards = searchData?.cards?.[journeyKey] || []
            const limitedIndices = journeyIndices.slice(0, initialWowSolutionsCount)
            const extractedCards = limitedIndices.map((index: number) => {
                const card = journeyCards[index]
                if (card?.subTravelOptionIds?.[0] && searchData?.subTravelOptions) {
                    const subTravelOptionId = card.subTravelOptionIds[0]
                    const subTravelOption = searchData.subTravelOptions[subTravelOptionId]
                    if (subTravelOption && !subTravelOptionsMap[subTravelOptionId]) {
                        subTravelOptionsMap[subTravelOptionId] = {
                            sequenceToFlightIdMap: subTravelOption.sequenceToFlightIdMap,
                            fareIds: subTravelOption.fareIds
                        }
                    }
                }
                return extractCardForWowPayload(card, searchData)
            })
            sortedCardData[journeyKey] = extractedCards
        })
    }

    // NOTE (UIENG-6303): backend no longer needs subTravelOptions / fares /
    // flights / travelOptionIdsToFareIdsMap in the request — `cards` plus
    // searchIntent / dataId is enough. The collected maps below are kept only
    // because the RT branch above still walks them for selectedCards extraction
    // (faresMap is populated as a side-effect of looking up the RT fareIds map);
    // we deliberately do NOT spread them into the final payload.
    void subTravelOptionsMap
    void faresMap
    void flightsMap
    void travelOptionIdsToFareIdsMapData
    void subTravelOptionCount

    const payload: Record<string, any> = {
        international: flowConditions?.isIntl || false,
        requestType: isDomesticOnewayNonCFW ? ['RPS', 'WOW_PRICE'] : ['WOW_PRICE'],
        sourceType: isInterstitial ? 'INTERSTITIAL' : 'SEARCH',
        trackingId: searchData?.trackingId || '',
        searchId: searchData?.searchId || '',
        dataId: searchData?.dataId || '',
        searchIntent: {
            ...(searchData?.searchIntent || {})
        },
        cards: sortedCardData,
        supercoins,
        ...(isWowTest2CleartripVariant(
            flowConditions?.wowPricingVariant,
            !!flowConditions?.isIntl
        ) && {
            enableWowSavingsOnIntl: true
        })
    }

    return payload
}

/**
 * Convert selectedFlights ([onward, return]) into the J1/J2 shape
 * `getFetchContentPayload` expects when `useSelectedCards` is in play.
 */
export function convertSelectedFlightsToSelectedCards(
    selectedFlights: any[]
): Record<string, any[]> {
    const selectedCards: Record<string, any[]> = {}
    if (selectedFlights && selectedFlights.length > 0) {
        if (selectedFlights[0]) selectedCards['J1'] = [selectedFlights[0]]
        if (selectedFlights[1]) selectedCards['J2'] = [selectedFlights[1]]
    }
    return selectedCards
}
